import {
  ISingleResource,
  ISuppliersResourceCategory,
  ITherapySuppliersResult,
  getExpertise,
  getSupplierNames,
  getSupplierResources,
  getTreatmentMethods,
} from 'API/Suppliers/suppliers';
import { getGeneralOptions } from 'API/generalOptions/generalOptions';
import {
  SUPPLIERS_ID,
  SUPPLIERS_LOCATION_STEPS,
  SUPPLIERS_STEPS,
} from 'Enum/suppliers';
import { useDispatchApp } from 'Hooks';
import { ITherapySuppliers } from 'Interfaces/therapySuppliers';
import { setGeneralOptions } from 'Store';
import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { IAction, SuppliersReducer } from 'reducer/Suppliers/SuppliersReducer';

export const resultsUrl = 'results';

export interface ISuppliers {
  results: ITherapySuppliersResult[];
  filteredResults: ITherapySuppliersResult[];
}

export interface ICoordinate {
  latitude: number;
  longitude: number;
}

export interface ITherapyFilters extends ITherapySuppliers {
  orderByAscendingAlphabet?: boolean;
  orderByDescendingAlphabet?: boolean;
  location: ICoordinate | null;
}

interface ISuppliersContext {
  suppliersId: number;
  setSuppliersId: (index: number) => void;
  stepIndex: number;
  setStepIndex: (index: number) => void;
  suppliers: ISuppliers;
  dispatchSuppliers: React.Dispatch<IAction>;
  resources: ISuppliersResourceCategory;
  supplierNames: string[];
  expertise: Partial<ISingleResource>;
  treatmentMethods: Partial<ISingleResource>;
  filters: Partial<ITherapyFilters>;
  setFilters: (filters: Partial<ITherapyFilters>) => void;
  resetFiltersOnSpecialtyChange: (specialty: number) => void;
  locationStepId: string;
  setLocationStepId: (locationStepId: string) => void;
  userLocation: ICoordinate | null;
}

export const SuppliersContext = createContext<ISuppliersContext>({
  suppliersId: 0,
  setSuppliersId: () => {},
  stepIndex: 0,
  setStepIndex: () => {},
  suppliers: {
    results: [],
    filteredResults: [],
  },
  dispatchSuppliers: () => {},
  resources: {},
  supplierNames: [],
  expertise: {},
  treatmentMethods: {},
  filters: {},
  setFilters: () => {},
  resetFiltersOnSpecialtyChange: () => {},
  locationStepId: '',
  setLocationStepId: () => {},
  userLocation: null,
});

export const defaultFilters = {
  supName: '',
  specialty: null,
  expertise: [],
  area: null,
  region: null,
  city: [],
  accessible: null,
  gender: null,
  homeVisit: null,
  instituteId: null,
  lng: [],
  treatMethod: [],
  location: null,
};

export const SuppliersProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [suppliersId, setSuppliersId] = useState<number>(
    SUPPLIERS_ID.MENTAL_THERAPY
  );
  const [stepIndex, setStepIndex] = useState<number>(SUPPLIERS_STEPS.LOBBY);
  const [suppliers, dispatchSuppliers] = useReducer(SuppliersReducer, {
    results: [],
    filteredResults: [],
  });
  const [resources, setResources] = useState<ISuppliersResourceCategory>({});
  const [supplierNames, setSupplierNames] = useState<string[]>([]);
  const [expertise, setExpertise] = useState<Partial<ISingleResource>>({});
  const [treatmentMethods, setTreatmentMethods] = useState<
    Partial<ISingleResource>
  >({});
  const [filters, setFilters] = useState<Partial<ITherapySuppliers>>({
    ...defaultFilters,
  });
  const [locationStepId, setLocationStepId] = useState<string>(
    SUPPLIERS_LOCATION_STEPS.city
  );
  const [userLocation, setUserLocation] = useState<ICoordinate | null>(null);

  const dispatch = useDispatchApp();

  const initResources = useCallback(async () => {
    const resources = await getSupplierResources();
    if (resources) setResources(resources);
  }, [setResources]);

  const initGeneralOptions = useCallback(async () => {
    const newGeneralOptions = await getGeneralOptions();
    dispatch(setGeneralOptions(newGeneralOptions ?? null));
  }, [dispatch]);

  useEffect(() => {
    initResources();
    initGeneralOptions();
  }, [initGeneralOptions, initResources]);

  const initSupplierNames = useCallback(async () => {
    const newNames = await getSupplierNames(filters.specialty!);
    if (newNames) setSupplierNames(newNames);
  }, [filters.specialty]);

  const initExpertise = useCallback(async () => {
    const newExpertise = await getExpertise(filters.specialty!);
    if (newExpertise) setExpertise(newExpertise);
  }, [filters.specialty]);

  const initTreatmentMethods = useCallback(async () => {
    const newTreatmentMethods = await getTreatmentMethods(filters.specialty!);
    if (newTreatmentMethods) setTreatmentMethods(newTreatmentMethods);
  }, [filters.specialty]);

  const initUserLocation = useCallback(async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          //TODO Error Handler View
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    }
  }, []);

  const init = useCallback(async () => {
    if (filters.specialty) {
      await Promise.all([
        initSupplierNames(),
        initExpertise(),
        initTreatmentMethods(),
      ]);
    } else {
      setSupplierNames([]);
      setExpertise({});
      setTreatmentMethods({});
    }
  }, [
    filters.specialty,
    initExpertise,
    initSupplierNames,
    initTreatmentMethods,
  ]);

  useEffect(() => {
    init();
    initUserLocation();
  }, [init, initUserLocation]);

  const resetFiltersOnSpecialtyChange = (specialty: number) => {
    setFilters({
      supName: '',
      specialty: specialty,
      expertise: [],
      area: null,
      region: null,
      city: [],
      accessible: null,
      gender: null,
      homeVisit: null,
      instituteId: null,
      lng: [],
      treatMethod: [],
    });
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (stepIndex === SUPPLIERS_STEPS.RESULTS && !filters.specialty) {
      setStepIndex(SUPPLIERS_STEPS.FORM);
      navigate(window.location.pathname.replace(`/${resultsUrl}`, ''));
    }
  }, [filters.specialty, navigate, stepIndex]);

  return (
    <SuppliersContext.Provider
      value={{
        suppliersId,
        setSuppliersId,
        stepIndex,
        setStepIndex,
        suppliers,
        dispatchSuppliers,
        resources,
        supplierNames,
        expertise,
        treatmentMethods,
        filters,
        setFilters,
        resetFiltersOnSpecialtyChange,
        locationStepId,
        setLocationStepId,
        userLocation,
      }}
    >
      {children}
    </SuppliersContext.Provider>
  );
};
